import { FC, useEffect, useMemo, useState } from 'react';
import { Checkbox, Radio } from 'antd';
import { useTranslation } from 'react-i18next';
import { PrinterOutlined } from '@ant-design/icons';
import Button from 'Components/UI/Button';
import Modal from 'Components/UI/Modal';
import { Icon } from 'Components/UI';
import {
    ActivitiesOption,
    ActivitiesRadioLabel,
    ActivitiesTag,
    Body,
    Content,
    FooterButtons,
    FooterDivider,
    OrientationLabel,
    OrientationOption,
    OrientationOptions,
    Section,
    SectionLabel,
    StyledCheckbox,
    StyledRadio,
} from './styles';

export type TypePrintOrientation = 'portrait' | 'landscape';
export type TypePrintActivitiesScope = 'filtered' | 'all';

export interface IPrintDetailsSettings {
    activitiesScope: TypePrintActivitiesScope;
    orientation: TypePrintOrientation;
    includeBasicInfo: boolean;
    includeAttachments: boolean;
    includeComments: boolean;
    includeHistory: boolean;
}

interface IPrintDetailsModal {
    visible: boolean;
    onCancel: () => void;
    onPrint: (settings: IPrintDetailsSettings) => void;
    hasFiltersApplied: boolean;
    filteredActivitiesCount: number;
    totalActivitiesCount: number;
}

const PrintDetailsModal: FC<IPrintDetailsModal> = ({
    visible,
    onCancel,
    onPrint,
    hasFiltersApplied,
    filteredActivitiesCount,
    totalActivitiesCount,
}) => {
    const { t } = useTranslation('activities');
    const [activitiesScope, setActivitiesScope] = useState<TypePrintActivitiesScope>('filtered');
    const [orientation, setOrientation] = useState<TypePrintOrientation>('portrait');
    const [includeBasicInfo, setIncludeBasicInfo] = useState(true);
    const [includeAttachments, setIncludeAttachments] = useState(true);
    const [includeComments, setIncludeComments] = useState(true);
    const [includeHistory, setIncludeHistory] = useState(true);

    const formatCount = (count: number) => {
        return new Intl.NumberFormat('pt-BR').format(count);
    };

    const hasSelectedData = useMemo(() => {
        return includeBasicInfo || includeAttachments || includeComments || includeHistory;
    }, [includeAttachments, includeBasicInfo, includeComments, includeHistory]);

    useEffect(() => {
        if (!hasFiltersApplied) {
            setActivitiesScope('all');
        }
    }, [hasFiltersApplied]);

    const handlePrint = () => {
        onPrint({
            activitiesScope,
            orientation,
            includeBasicInfo,
            includeAttachments,
            includeComments,
            includeHistory,
        });
    };

    const footerButtons = (
        <FooterButtons>
            <Button
                type="text"
                onClick={onCancel}
                className="buttonCancel"
                height={32}
            >
                {t('printDetails.cancel')}
            </Button>
            <Button
                type="primary"
                onClick={handlePrint}
                className="buttonPrint"
                height={32}
                disabled={!hasSelectedData}
            >
                <PrinterOutlined className="buttonPrintIcon" />
                {t('printDetails.print')}
            </Button>
        </FooterButtons>
    );

    return (
        <Modal
            visible={visible}
            onCancel={onCancel}
            width={400}
            title={t('printDetails.title')}
            newHeader
            footerButtons={footerButtons}
            footerActive={false}
            maskClosable={false}
        >
            <Body>
                <Content>
                    <Section>
                        <SectionLabel>
                            <span>{t('printDetails.issues')}</span>
                            <strong>*</strong>
                        </SectionLabel>
                        <StyledCheckbox>
                            <ActivitiesOption
                                onClick={() => setActivitiesScope('filtered')}
                                disabled={!hasFiltersApplied}
                            >
                                <ActivitiesRadioLabel>
                                    <StyledRadio>
                                        <Radio checked={activitiesScope === 'filtered'} />
                                    </StyledRadio>
                                    <span>{t('printDetails.onlyFiltered')}</span>
                                    {hasFiltersApplied && (
                                        <ActivitiesTag>{formatCount(filteredActivitiesCount)}</ActivitiesTag>
                                    )}
                                </ActivitiesRadioLabel>
                            </ActivitiesOption>
                            <ActivitiesOption onClick={() => setActivitiesScope('all')}>
                                <ActivitiesRadioLabel>
                                    <StyledRadio>
                                        <Radio checked={activitiesScope === 'all'} />
                                    </StyledRadio>
                                    <span>{t('printDetails.allIssues')}</span>
                                    <ActivitiesTag>{formatCount(totalActivitiesCount)}</ActivitiesTag>
                                </ActivitiesRadioLabel>
                            </ActivitiesOption>
                        </StyledCheckbox>
                    </Section>

                    <Section>
                        <SectionLabel>
                            <span>{t('printDetails.orientation')}</span>
                            <strong>*</strong>
                        </SectionLabel>
                        <OrientationOptions>
                            <OrientationOption
                                selected={orientation === 'portrait'}
                                onClick={() => setOrientation('portrait')}
                            >
                                <StyledRadio>
                                    <Radio checked={orientation === 'portrait'} />
                                </StyledRadio>
                                <Icon icon="arquivo" className="orientationIcon" customSize={14} />
                                <OrientationLabel>{t('printDetails.portrait')}</OrientationLabel>
                            </OrientationOption>
                            <OrientationOption
                                selected={orientation === 'landscape'}
                                onClick={() => setOrientation('landscape')}
                            >
                                <StyledRadio>
                                    <Radio checked={orientation === 'landscape'} />
                                </StyledRadio>
                                <Icon
                                    icon="arquivo"
                                    className="orientationIcon orientationIconLandscape"
                                    customSize={14}
                                />
                                <OrientationLabel>{t('printDetails.landscape')}</OrientationLabel>
                            </OrientationOption>
                        </OrientationOptions>
                    </Section>

                    <Section>
                        <SectionLabel>
                            <span>{t('printDetails.reportData')}</span>
                            <strong>*</strong>
                        </SectionLabel>
                        <StyledCheckbox>
                            <Checkbox
                                checked={includeBasicInfo}
                                onChange={(event) => setIncludeBasicInfo(event.target.checked)}
                            >
                                {t('printDetails.basicInfo')}
                            </Checkbox>
                            <Checkbox
                                checked={includeAttachments}
                                onChange={(event) => setIncludeAttachments(event.target.checked)}
                            >
                                {t('printDetails.attachments')}
                            </Checkbox>
                            <Checkbox
                                checked={includeComments}
                                onChange={(event) => setIncludeComments(event.target.checked)}
                            >
                                {t('printDetails.comments')}
                            </Checkbox>
                            <Checkbox
                                checked={includeHistory}
                                onChange={(event) => setIncludeHistory(event.target.checked)}
                            >
                                {t('printDetails.history')}
                            </Checkbox>
                        </StyledCheckbox>
                    </Section>
                </Content>
            </Body>
            <FooterDivider />
        </Modal>
    );
};

export default PrintDetailsModal;
